import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, CreditCard, CheckCircle2 } from "lucide-react";
import { useI18n } from "../i18n";
import SEO from "../components/SEO";
import { COMPANY } from "../mock";

const API = process.env.REACT_APP_BACKEND_URL;

export default function EstimatePay() {
  const { id } = useParams();
  const { t } = useI18n();
  const [est, setEst] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API}/api/estimates/${id}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setEst)
      .catch(() => setNotFound(true));
  }, [id]);

  const pay = async () => {
    setPaying(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/estimates/${id}/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      if (!res.ok) throw new Error();
      const { checkout_url } = await res.json();
      window.location.href = checkout_url;
    } catch {
      setError(t({ es: "No se pudo iniciar el pago. Intente de nuevo.", en: "Could not start the payment. Please try again." }));
      setPaying(false);
    }
  };

  return (
    <section className="section" style={{ paddingTop: 160, minHeight: "75vh" }}>
      <SEO title={{ es: "Pago de presupuesto", en: "Estimate payment" }} path={`/presupuesto/${id}`} description={{ es: "Pague su presupuesto de forma segura con tarjeta a través de Stripe.", en: "Pay your estimate securely by card via Stripe." }} noindex />
      <div className="container-x" style={{ maxWidth: 560 }}>
        {notFound ? (
          <div className="card" style={{ padding: 36, textAlign: "center" }} data-testid="estimate-not-found">
            <h2>{t({ es: "Presupuesto no encontrado", en: "Estimate not found" })}</h2>
            <p style={{ color: "var(--muted)" }}>{t({ es: "Verifique el enlace o contáctenos por WhatsApp.", en: "Check the link or contact us via WhatsApp." })}</p>
          </div>
        ) : !est ? (
          <div className="card" style={{ padding: 36, textAlign: "center" }}>{t({ es: "Cargando...", en: "Loading..." })}</div>
        ) : (
          <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="estimate-pay-card">
            <div style={{ background: "var(--navy)", color: "#fff", padding: "22px 28px" }}>
              <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, letterSpacing: ".04em", fontSize: 20 }}>{COMPANY.shortName.toUpperCase()}</div>
              <div style={{ opacity: 0.75, fontSize: 13 }}>{t({ es: "Presupuesto", en: "Estimate" })} #{est.id.slice(0, 8).toUpperCase()}</div>
            </div>
            <div style={{ padding: 28 }}>
              <p style={{ fontSize: 14, color: "var(--muted)", marginBottom: 4 }}>{t({ es: "Cliente", en: "Client" })}</p>
              <p style={{ fontWeight: 700 }} data-testid="estimate-client">{est.cliente}{est.empresa ? ` — ${est.empresa}` : ""}</p>
              <p style={{ fontSize: 14, color: "var(--muted)", margin: "16px 0 4px" }}>{t({ es: "Concepto", en: "Description" })}</p>
              <p data-testid="estimate-concept">{est.concepto}</p>
              <div style={{ borderTop: "1px solid rgba(11,31,58,0.1)", margin: "20px 0", paddingTop: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontWeight: 700 }}>{t({ es: "Total", en: "Total" })}</span>
                <span data-testid="estimate-amount" style={{ fontSize: 30, fontWeight: 800, color: "var(--navy)" }}>${Number(est.monto).toFixed(2)} <small style={{ fontSize: 14, fontWeight: 600 }}>USD</small></span>
              </div>
              {est.status === "paid" ? (
                <div data-testid="estimate-paid-badge" style={{ background: "#e7f8ee", color: "#1e8e4e", padding: 16, borderRadius: 12, textAlign: "center", fontWeight: 700 }}>
                  <CheckCircle2 size={18} style={{ display: "inline", verticalAlign: "-4px", marginRight: 6 }} />
                  {t({ es: "Este presupuesto ya fue pagado. ¡Gracias!", en: "This estimate has already been paid. Thank you!" })}
                </div>
              ) : (
                <>
                  <button data-testid="pay-now-button" onClick={pay} disabled={paying} className="btn btn-navy" style={{ width: "100%", justifyContent: "center", padding: "14px 0", fontSize: 16 }}>
                    <CreditCard size={18} /> {paying ? t({ es: "Redirigiendo...", en: "Redirecting..." }) : t({ es: "Pagar ahora", en: "Pay now" })}
                  </button>
                  {error && <p data-testid="pay-error" style={{ color: "#c0392b", fontSize: 14, marginTop: 10, textAlign: "center" }}>{error}</p>}
                  <p style={{ fontSize: 12, color: "var(--muted)", textAlign: "center", marginTop: 14 }}>
                    <ShieldCheck size={14} style={{ display: "inline", verticalAlign: "-3px", marginRight: 4 }} />
                    {t({ es: "Pago seguro procesado por Stripe. Aceptamos tarjetas de crédito y débito.", en: "Secure payment processed by Stripe. We accept credit and debit cards." })}
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
